import { Popover, Grid } from '@mui/material';
import debounce from 'lodash.debounce';
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';

import FormikForm from '../Formik/Form/FormikForm';
import { ODSSearchField } from '../InputFields';

import type { FormikFormField } from '@/types/formTypes';
import type { TableField, TableRowData } from '@/types/tableTypes';

interface FormikField {
  fieldType: string;
  name: string;
  label: string;
  menuItems: { value: string; label: string }[];
  'data-testid'?: string;
}

type Filters = Record<string, string | string[]>;

interface SearchProps {
  setCurrentPage: (page: number) => void;
  setSearchedRows: (rows: TableRowData[]) => void;
  rows: TableRowData[];
  fields: TableField[];
  advancedSearch: boolean;
  enablePaginatedSearch?: boolean;
  setFilter?: (filter: string) => void;
  setAdvancedFieldFilter?: (filter: Record<string, string | string[]>) => void;
}

const Search = ({
  setCurrentPage,
  setSearchedRows,
  rows,
  fields,
  advancedSearch,
  enablePaginatedSearch = false,
  setFilter,
  setAdvancedFieldFilter,
}: SearchProps) => {
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<{ resetForm: (options: { values: Filters }) => void }>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [displayValue, setDisplayValue] = useState<string>('');

  const initialFilters = useMemo<Filters>(
    () =>
      fields.reduce<Filters>(
        (acc, field) => ({
          ...acc,
          [field.id]: [],
        }),
        { search: '' }
      ),
    [fields]
  );

  const [filters, setFilters] = useState<Filters>(initialFilters);

  const hasFilters = useMemo<boolean>(
    () =>
      Object.entries(filters).some(
        ([key, value]) =>
          key !== 'search' && Array.isArray(value) && value.length > 0 && value[0] !== ''
      ),
    [filters]
  );

  const filterRows = useCallback(
    (query: string): TableRowData[] => {
      const lowerQuery = query.toLowerCase();

      return rows.filter(row =>
        row.data.some(({ value }) => {
          const lowerValue = String(value).toLowerCase();
          return lowerValue.includes(lowerQuery);
        })
      );
    },
    [rows]
  );

  useEffect(() => {
    if (!hasFilters) {
      const searchValue = filters.search as string;
      setSearchedRows(searchValue.length > 2 ? filterRows(searchValue) : rows);
    }
  }, [filters.search, rows, hasFilters, filterRows, setSearchedRows]);

  const extractMenuItems = useCallback(
    (fieldId: string): { value: string; label: string }[] => {
      const uniqueValues = new Set<string>(
        rows.flatMap(({ data }) =>
          data
            .filter(({ field, value }) => field === fieldId && value)
            .flatMap(({ value }): string[] =>
              Array.isArray(value) ? (value.filter(Boolean) as string[]) : [String(value)]
            )
        )
      );

      return [...uniqueValues]
        .map(value => ({ value: value, label: value }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [rows]
  );

  const joinFilters = useMemo<string>(() => {
    const filterEntries = Object.entries(filters)
      .filter(([key, value]) => {
        if (key === 'search') return false;
        return Array.isArray(value) && value.length > 0 && String(value[0]).startsWith('');
      })
      .map(([key, value]) => {
        const field = fields.find(f => f.id === key);
        const fieldName = field ? field.title : key;
        return `${fieldName}: ${(value as string[]).join(', ')}`;
      });

    return filterEntries.join('; ');
  }, [filters, fields]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(prev => ({ ...prev, search: value }));
        setFilter?.(value);
        setCurrentPage(0);
      }, 250),
    [setFilter, setCurrentPage]
  );

  useEffect(
    () => () => {
      debouncedSetFilter.cancel();
    },
    [debouncedSetFilter]
  );

  const filterRowByFields = (
    row: TableRowData,
    fields: TableField[],
    updatedFilters: Filters
  ): boolean => {
    return fields.every(field => {
      const filterValues = updatedFilters[field.id];
      if (!filterValues || filterValues.length === 0) {
        return true; // No filter applied for this field
      }

      const cellData = row.data.find(cell => cell.field === field.id);
      if (!cellData) {
        return false; // No matching field in the row
      }

      if (Array.isArray(cellData.value)) {
        // If the cell value is an array, check if any value matches the filter
        return cellData.value.some(value => filterValues.includes(String(value)));
      }

      // Otherwise, check if the single value matches the filter
      return filterValues.includes(String(cellData.value));
    });
  };

  const filterRowsByAdvancedSearch = useCallback(
    (updatedFilters: Filters): TableRowData[] =>
      rows.filter(row => filterRowByFields(row, fields, updatedFilters)),
    [fields, rows]
  );

  const formikFields = useMemo<FormikField[]>(() => {
    const filteredFields = enablePaginatedSearch
      ? fields.filter(field => field.isAdvancedSearchable)
      : fields;

    return filteredFields.map(({ id, title, ...fieldRest }) => {
      let fieldType = 'text';
      let menuItems: { value: string; label: string }[] = [];

      const fieldTypeFromRest = (fieldRest as { type?: string }).type;
      if (fieldTypeFromRest === 'options') {
        menuItems = extractMenuItems(id);
        fieldType = menuItems.length > 2 ? 'multiSelect' : 'select';
      }

      return {
        fieldType,
        name: id,
        label: title,
        menuItems,
      };
    });
  }, [fields, extractMenuItems, enablePaginatedSearch]);

  const getInitialFormikValues = useCallback((): Filters => {
    const initialValues = fields.reduce<Filters>(
      (acc, { id }) => {
        const fieldValue = formikFields.find(f => f.name === id);
        const value =
          fieldValue?.fieldType === 'multiSelect' ? (filters[id] ?? []) : (filters[id]?.[0] ?? '');

        return { ...acc, [id]: value };
      },
      { search: filters.search ?? '' }
    );

    return initialValues;
  }, [fields, filters, formikFields]);

  const handleEagerSearch = (updatedFilters: Filters) => {
    const newFilters = { ...filters, ...updatedFilters, search: '' };

    const newJoinedFilters = Object.entries(newFilters)
      .filter(([key, value]) => {
        if (key === 'search' || key === 'data-testid') return false;
        return Array.isArray(value) && value.length > 0 && String(value[0]).startsWith('');
      })
      .map(([key, value]) => {
        const field = fields.find(f => f.id === key);
        const fieldName = field ? field.title : key;
        return `${fieldName}: ${(value as unknown as string[]).join(', ')}`;
      })
      .join('; ');

    setFilters(newFilters);
    setDisplayValue(newJoinedFilters);
    setSearchedRows(filterRowsByAdvancedSearch(updatedFilters));
  };

  const handleApplyFilters = (values: Record<string, unknown>): void => {
    const filteredValues = Object.fromEntries(
      Object.entries(values).filter(([key]) => key !== 'search' && key !== 'data-testid')
    );

    const updatedFilters = Object.fromEntries(
      Object.entries(filteredValues).map(([key, value]) =>
        Array.isArray(value)
          ? [key, (value as string[]).filter(Boolean)]
          : [key, value ? [value] : []]
      )
    ) as Filters;

    if (!enablePaginatedSearch) {
      handleEagerSearch(updatedFilters);
    } else {
      setFilters(prev => ({ ...prev, ...updatedFilters, search: '' }));
      setAdvancedFieldFilter?.(updatedFilters);
    }
    setCurrentPage(0);
    setAnchorEl(null);
  };

  const handleClearFilters = (): void => {
    formRef.current?.resetForm({ values: initialFilters });
    if (enablePaginatedSearch) {
      setAdvancedFieldFilter?.({});
    }
    setSearchedRows(rows);
    setFilters(initialFilters);
    setCurrentPage(0);
    setSearchInput('');
    setDisplayValue('');
  };

  useEffect(() => {
    if (!hasFilters) {
      setDisplayValue(searchInput);
    }
  }, [hasFilters, searchInput]);

  useEffect(() => {
    if (hasFilters && joinFilters) {
      setDisplayValue(joinFilters);
    }
  }, [hasFilters, joinFilters]);

  return (
    <>
      <ODSSearchField
        name='search'
        label={hasFilters ? '' : 'Search'}
        value={displayValue}
        disabled={hasFilters}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          if (!hasFilters) {
            setSearchInput(value);
            setDisplayValue(value);
            debouncedSetFilter(value);
          }
        }}
        inputRef={searchFieldRef}
        {...(advancedSearch && {
          onFilterClick: () => {
            setAnchorEl(searchFieldRef.current);
          },
        })}
        advancedSearch={advancedSearch}
      />
      {advancedSearch && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Grid
            container
            p={2}
          >
            <FormikForm
              formRef={formRef}
              fields={formikFields as FormikFormField[]}
              initialValues={{ ...getInitialFormikValues(), 'data-testid': 'initial-values' }}
              onSubmit={handleApplyFilters}
              onCancel={handleClearFilters}
              submitLabel='Apply'
              cancelLabel='Clear'
            />
          </Grid>
        </Popover>
      )}
    </>
  );
};

export default Search;
